import React, { useState, useEffect } from "react";
import { motion, useAnimation } from "framer-motion";
import { Parallax } from "react-scroll-parallax";
import { useInView } from "react-intersection-observer";
import emailjs from "emailjs-com";

function Contact() {
  const controls = useAnimation();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });

  const [form, setForm] = useState({
    name: "",
    email: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [statusMessage, setStatusMessage] = useState("");

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  const containerVariants = {
    visible: { transition: { staggerChildren: 0.2 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0 },
  };

  const validate = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = "Name is required.";
    if (!form.email.trim()) {
      newErrors.email = "Email is required.";
    } else if (!/^[a-zA-Z0-9._%+-]+@gmail\.com$/.test(form.email)) {
      newErrors.email = "Email must be a valid @gmail.com address.";
    }
    if (!form.message.trim()) newErrors.message = "Message is required.";
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validate();
    setErrors(validationErrors);
    setStatusMessage("");

    if (Object.keys(validationErrors).length === 0) {
      emailjs
        .send(
          "service_oudkww9", // <-- replace with your EmailJS Service ID
          "template_hn2xyka", // <-- replace with your EmailJS Template ID
          {
            from_name: form.name,
            from_email: form.email,
            message: form.message,
          },
          "3IJ7I9wc_CoVAiVjH" // <-- replace with your EmailJS Public Key
        )
        .then(
          () => {
            setStatusMessage("Message sent successfully!");
            setForm({ name: "", email: "", message: "" });
          },
          (error) => {
            setStatusMessage("Failed to send message. Please try again.");
            console.error("EmailJS Error:", error);
          }
        );
    }
  };

  return (
    <section
      id="contact"
      className="bg-transparent text-white min-h-screen snap-start flex items-center justify-center px-4 py-12 sm:py-16 lg:py-24"
    >
      <div className="w-full max-w-4xl" ref={ref}>
        <Parallax y={[-20, 20]} tagOuter="figure">
          <motion.h2
            className="font-pixelTitle text-3xl sm:text-4xl md:text-5xl font-bold text-green mb-4 text-center"
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.8 }}
          >
            Contact Me
          </motion.h2>
          <motion.p
            className="font-pixelBody text-base sm:text-lg md:text-xl text-gray-400 mb-8 text-center px-2"
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            I'd love to hear from you! Whether you want to discuss a project or
            just chat about tech, feel free to reach out.
          </motion.p>
        </Parallax>

        <motion.form
          className="w-full bg-black/40 backdrop-blur-md p-6 sm:p-8 rounded-xl shadow-xl"
          variants={containerVariants}
          initial="hidden"
          animate={controls}
          onSubmit={handleSubmit}
        >
          <motion.input
            type="text"
            placeholder="Your Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="font-pixelAccent w-full bg-gray-900 text-white border border-gray-700 p-3 mb-2 rounded-md focus:outline-none focus:ring-2 focus:ring-green"
            variants={itemVariants}
          />
          {errors.name && (
            <p className="text-red-500 text-sm mb-2">{errors.name}</p>
          )}

          <motion.input
            type="email"
            placeholder="Your Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="font-pixelAccent w-full bg-gray-900 text-white border border-gray-700 p-3 mb-2 rounded-md focus:outline-none focus:ring-2 focus:ring-green"
            variants={itemVariants}
          />
          {errors.email && (
            <p className="text-red-500 text-sm mb-2">{errors.email}</p>
          )}

          <motion.textarea
            placeholder="Your Message"
            rows="4"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            className="font-pixelAccent w-full bg-gray-900 text-white border border-gray-700 p-3 mb-2 rounded-md focus:outline-none focus:ring-2 focus:ring-green"
            variants={itemVariants}
          ></motion.textarea>
          {errors.message && (
            <p className="text-red-500 text-sm mb-2">{errors.message}</p>
          )}

          <motion.button
            type="submit"
            className="font-pixelBody w-full bg-green text-black font-semibold p-3 rounded-md mt-4 hover:bg-opacity-90 transition duration-200"
            whileHover={{ scale: 1.05 }}
            variants={itemVariants}
          >
            Send Message
          </motion.button>

          {statusMessage && (
            <p className="text-center mt-4 text-sm text-green-400">
              {statusMessage}
            </p>
          )}
        </motion.form>
      </div>
    </section>
  );
}

export default Contact;
